import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIRECTIONS = [
  { value: 'receivable', label: 'Accounts Receivable (Client → Me)' },
  { value: 'payable', label: 'Accounts Payable (Me → Vendor)' },
];

const PAYMENT_TYPES = [
  { value: 'planning_fee', label: 'Planning Fee' },
  { value: 'service_fee', label: 'Service Fee' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'installment', label: 'Installment' },
  { value: 'final_balance', label: 'Final Balance' },
  { value: 'vendor_payment', label: 'Vendor Payment' },
  { value: 'commission', label: 'Commission' },
  { value: 'refund', label: 'Refund' },
  { value: 'adjustment', label: 'Adjustment' },
];

const PAYMENT_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'waived', label: 'Waived' },
];

const PAYMENT_METHODS = [
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'bank_transfer', label: 'Bank Transfer / ACH' },
  { value: 'check', label: 'Check' },
  { value: 'cash', label: 'Cash' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'other', label: 'Other' },
];

export function PaymentFormModal({ open, onClose, onSaved, payment = null, trips = [], clients = [], bookings = [] }) {
  const isEdit = !!payment;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    trip_id: '', client_id: '', booking_id: '', invoice_id: '',
    direction: 'receivable', payment_type: 'planning_fee',
    amount: 0, currency: 'USD', description: '',
    due_date: '', paid_date: '', payment_method: '',
    status: 'pending', notes: '', client_visible: true,
  });

  useEffect(() => {
    if (payment) {
      setForm({
        trip_id: payment.trip_id || '',
        client_id: payment.client_id || '',
        booking_id: payment.booking_id || '',
        invoice_id: payment.invoice_id || '',
        direction: payment.direction || 'receivable',
        payment_type: payment.payment_type || 'planning_fee',
        amount: payment.amount || 0,
        currency: payment.currency || 'USD',
        description: payment.description || '',
        due_date: payment.due_date?.split('T')[0] || '',
        paid_date: payment.paid_date?.split('T')[0] || '',
        payment_method: payment.payment_method || '',
        status: payment.status || 'pending',
        notes: payment.notes || '',
        client_visible: payment.client_visible !== false,
      });
    } else {
      setForm({
        trip_id: '', client_id: '', booking_id: '', invoice_id: '',
        direction: 'receivable', payment_type: 'planning_fee',
        amount: 0, currency: 'USD', description: '',
        due_date: '', paid_date: '', payment_method: '',
        status: 'pending', notes: '', client_visible: true,
      });
    }
  }, [payment, open]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleTripChange = (tripId) => {
    handleChange('trip_id', tripId);
    const trip = trips.find(t => t.id === tripId);
    if (trip?.client_id) {
      handleChange('client_id', trip.client_id);
    }
  };

  const handleSave = async () => {
    if (!form.amount || form.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/payments/${payment.id}`, form);
        toast.success('Payment updated');
      } else {
        await axios.post(`${API}/payments`, form);
        toast.success('Payment recorded');
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save payment');
    } finally {
      setSaving(false);
    }
  };

  // Filter bookings by selected trip
  const tripBookings = form.trip_id
    ? bookings.filter(b => b.trip_id === form.trip_id)
    : bookings;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[var(--vt-bg-card)] border-[var(--vt-border)]">
        <DialogHeader>
          <DialogTitle className="font-serif text-[var(--vt-text)]">
            {isEdit ? 'Edit Payment' : 'Record Payment'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Direction (AR vs AP) */}
          <div>
            <Label className="text-[var(--vt-text-muted)] text-xs">Payment Direction *</Label>
            <Select value={form.direction} onValueChange={v => handleChange('direction', v)}>
              <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="payment-direction-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIRECTIONS.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Trip & Client */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Trip</Label>
              <Select value={form.trip_id} onValueChange={handleTripChange}>
                <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="payment-trip-select">
                  <SelectValue placeholder="Select trip" />
                </SelectTrigger>
                <SelectContent>
                  {trips.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.internal_trip_name || t.destinations || 'Untitled'} — {t.client_name || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">
                {form.direction === 'payable' ? 'Vendor / Payee' : 'Client'}
              </Label>
              <Select value={form.client_id} onValueChange={v => handleChange('client_id', v)}>
                <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="payment-client-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Linked Booking */}
          {tripBookings.length > 0 && (
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Linked Booking (optional)</Label>
              <Select value={form.booking_id} onValueChange={v => handleChange('booking_id', v)}>
                <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="payment-booking-select">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {tripBookings.map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.vendor} — {b.booking_type} ({b.confirmation_number || 'No ref'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Type & Amount */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Payment Type</Label>
              <Select value={form.payment_type} onValueChange={v => handleChange('payment_type', v)}>
                <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="payment-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Amount *</Label>
              <Input
                type="number" step="0.01"
                value={form.amount}
                onChange={e => handleChange('amount', parseFloat(e.target.value) || 0)}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="payment-amount-input"
              />
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Currency</Label>
              <Input
                value={form.currency}
                onChange={e => handleChange('currency', e.target.value)}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="payment-currency"
              />
            </div>
          </div>

          {/* Dates & Status */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Due Date</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={e => handleChange('due_date', e.target.value)}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="payment-due-date"
              />
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Date Paid</Label>
              <Input
                type="date"
                value={form.paid_date}
                onChange={e => handleChange('paid_date', e.target.value)}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="payment-paid-date"
              />
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Status</Label>
              <Select value={form.status} onValueChange={v => handleChange('status', v)}>
                <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="payment-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Payment Method */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Payment Method</Label>
              <Select value={form.payment_method || 'none'} onValueChange={v => handleChange('payment_method', v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="payment-method-select">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Description</Label>
              <Input
                value={form.description}
                onChange={e => handleChange('description', e.target.value)}
                placeholder="Brief description"
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="payment-description-input"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-[var(--vt-text-muted)] text-xs">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={e => handleChange('notes', e.target.value)}
              rows={2}
              className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
              data-testid="payment-notes"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[var(--vt-border)]">
            <Button variant="ghost" onClick={onClose} data-testid="payment-cancel-btn">Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[var(--vt-gold)] hover:bg-[var(--vt-gold)]/90 text-white"
              data-testid="payment-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isEdit ? 'Update Payment' : 'Record Payment'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
